/** Collapsible section showing recently modified documents */

import { ChevronRight, Clock, FileCheck2, FileText } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useAppStore } from '../../stores/app-store'
import { useDocuments } from '../../hooks/use-documents'
import { usePreferences, useSetPreference } from '../../hooks/use-preferences'
import { DocumentStatusBadge } from '../shared/document-status-badge'
import { getDocumentKindInfo } from '../../lib/document-kind'
import { useOpenDocumentRoute, type RoutableDocument } from '../../hooks/use-open-document-route'

/** Format a date as relative time (e.g., "2m ago", "1h ago", "3d ago") */
function relativeTime(date: Date | string): string {
  const now = Date.now()
  const then = new Date(date).getTime()
  const diffMs = now - then
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr < 24) return `${diffHr}h ago`
  if (diffDay < 30) return `${diffDay}d ago`
  return new Date(date).toLocaleDateString()
}

interface RecentDocumentsProps {
  onDocumentOpen?: () => void
}

export function RecentDocuments({ onDocumentOpen }: RecentDocumentsProps) {
  const { data: prefs } = usePreferences()
  const setPref = useSetPreference()
  const expanded = prefs?.sidebar_recent_collapsed !== 'true'

  const { data, isLoading } = useDocuments({ limit: 10, sort: 'updatedAt', order: 'desc' })
  const { theme } = useAppStore()
  const openDocumentRoute = useOpenDocumentRoute()

  const isDark = theme === 'dark'
  const docs = data?.data ?? []
  const verifiedDocs = docs.filter((doc) => getDocumentKindInfo(doc).kind === 'verified')
  const reviewDocs = docs.filter((doc) => {
    const kind = getDocumentKindInfo(doc).kind
    return kind === 'stale' || kind === 'conflict'
  })
  const draftDocs = docs.filter((doc) => getDocumentKindInfo(doc).kind === 'draft')

  // Only hide when confirmed empty, not while loading
  if (!isLoading && docs.length === 0) return null

  const toggleExpanded = () => {
    setPref.mutate({
      key: 'sidebar_recent_collapsed',
      value: expanded ? 'true' : 'false',
    })
  }

  const handleOpenDoc = (doc: RoutableDocument) => {
    openDocumentRoute(doc)
    onDocumentOpen?.()
  }

  return (
    <div
      aria-label="Recent documents"
      className={cn('border-b', isDark ? 'border-white/[0.06]' : 'border-neutral-200')}
    >
      {/* Header */}
      <button
        onClick={toggleExpanded}
        className={cn(
          'flex w-full items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider',
          isDark
            ? 'text-neutral-500 hover:text-neutral-400'
            : 'text-neutral-400 hover:text-neutral-600',
        )}
      >
        <ChevronRight
          className={cn('h-2.5 w-2.5 transition-transform duration-150', expanded && 'rotate-90')}
        />
        <Clock className="h-2.5 w-2.5" />
        Recent knowledge
      </button>

      {/* Document list */}
      {expanded && (
        <div className="space-y-0.5 px-1 pb-1.5">
          <RecentDocumentGroup
            label="Source-backed knowledge"
            docs={verifiedDocs}
            isDark={isDark}
            onOpen={handleOpenDoc}
          />
          <RecentDocumentGroup
            label="Needs review"
            docs={reviewDocs}
            isDark={isDark}
            onOpen={handleOpenDoc}
          />
          <RecentDocumentGroup
            label="Manual documents"
            docs={draftDocs}
            isDark={isDark}
            onOpen={handleOpenDoc}
          />
        </div>
      )}
    </div>
  )
}

function RecentDocumentGroup({
  label,
  docs,
  isDark,
  onOpen,
}: {
  label: string
  docs: Array<{
    id: string
    title: string
    slug: string
    updatedAt: Date | string
    sourceProvenance?: import('@agentwiki/shared').DocumentSourceProvenance | null
  }>
  isDark: boolean
  onOpen: (doc: RoutableDocument) => void
}) {
  if (docs.length === 0) return null

  return (
    <section className="space-y-0.5">
      <div
        className={cn(
          'px-2 pt-1 text-[10px] font-medium',
          isDark ? 'text-neutral-600' : 'text-neutral-400',
        )}
      >
        {label}
      </div>
      {docs.map((doc) => {
        const kind = getDocumentKindInfo(doc)
        const Icon = kind.kind === 'draft' ? FileText : FileCheck2
        return (
          <div
            key={doc.id}
            className={cn(
              'flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-xs',
              isDark
                ? 'text-neutral-400 hover:bg-surface-3 hover:text-neutral-200'
                : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800',
            )}
            onClick={() => onOpen(doc)}
          >
            <Icon
              className={cn(
                'h-3 w-3 shrink-0',
                kind.kind === 'draft' ? 'text-neutral-500' : 'text-brand-400',
              )}
            />
            <span className="min-w-0 flex-1 truncate">{doc.title}</span>
            <DocumentStatusBadge doc={doc} compact className="hidden xl:inline-flex" />
            <span
              className={cn(
                'shrink-0 text-[10px]',
                isDark ? 'text-neutral-600' : 'text-neutral-400',
              )}
            >
              {relativeTime(doc.updatedAt)}
            </span>
          </div>
        )
      })}
    </section>
  )
}
